import type geojson from "geojson";
import type { Feature } from "geojson";

import { api } from "./api";
import type { TLegendItem } from "./fieldsService";
import { readNumericProperty } from "./boundaryFileService";

// ============================================================
// Custom field layers — maps the customer brought themselves
// (uploaded KML/KMZ/zipped shapefile, or a John Deere prescription).
//
// The file is parsed in the browser by boundaryFileService; this module turns
// the chosen numeric attribute into the flat { geometry, value } list the
// backend stores, and reads layers back in the same
// { features, legend, stats } shape the yield and camera layers use.
// ============================================================

export type TCustomLayerSource = "file" | "johndeere";

export type TCustomLayerStats = {
  min: number;
  max: number;
  avg: number;
  count: number;
  areaHectares: number | null;
};

/** Metadata row from the list endpoint — never carries the geometry. */
export type TCustomLayer = {
  id: string;
  name: string;
  source: TCustomLayerSource;
  sourceFilename: string | null;
  valueProperty: string | null;
  unitLabel: string | null;
  featureCount: number;
  hasValues: boolean;
  stats: TCustomLayerStats | null;
  createdAt?: string;
};

export type TCustomLayerData = {
  id: string;
  name: string;
  unitLabel: string | null;
  valueProperty: string | null;
  features: geojson.FeatureCollection;
  legend: Array<TLegendItem>;
  stats: TCustomLayerStats | null;
};

/**
 * Payload size guard.
 *
 * /api/v1/* is capped at 10 MB server-side. Checking here turns an opaque 413
 * into a message that tells the user what to do about it.
 */
export const MAX_CUSTOM_LAYER_PAYLOAD_BYTES = 9 * 1024 * 1024;

export type TCustomLayerPayload = {
  name: string;
  source: TCustomLayerSource;
  sourceFilename?: string;
  valueProperty?: string | null;
  unitLabel?: string | null;
  externalRef?: string;
  features: Array<{ geometry: geojson.Geometry; value?: number | null }>;
};

/**
 * Reduce parsed features to what the backend stores. Dropping the original
 * property bag is deliberate: it is the bulk of a prescription file's size and
 * nothing renders it.
 */
export const buildCustomLayerFeatures = (
  features: Feature[],
  valueProperty: string | null
): TCustomLayerPayload["features"] =>
  features.map((f) => ({
    geometry: f.geometry,
    value: valueProperty ? readNumericProperty(f, valueProperty) : null,
  }));

export const listCustomLayers = async (fieldId: string): Promise<TCustomLayer[]> => {
  try {
    const res = await api.get<{ layers: TCustomLayer[] }>(
      `/api/v1/fields/${fieldId}/custom-layers`
    );
    return res.layers ?? [];
  } catch (e) {
    // A field with no layers must not break the map; the panel just shows empty.
    return [];
  }
};

export const getCustomLayer = async (
  fieldId: string,
  layerId: string
): Promise<TCustomLayerData> =>
  api.get<TCustomLayerData>(`/api/v1/fields/${fieldId}/custom-layers/${layerId}`);

export const createCustomLayer = async (
  fieldId: string,
  payload: TCustomLayerPayload
): Promise<TCustomLayer> =>
  api.post<TCustomLayer>(`/api/v1/fields/${fieldId}/custom-layers`, payload);

export const renameCustomLayer = async (
  fieldId: string,
  layerId: string,
  patch: { name?: string; unitLabel?: string | null }
): Promise<{ ok: boolean }> =>
  api.patch<{ ok: boolean }>(
    `/api/v1/fields/${fieldId}/custom-layers/${layerId}`,
    patch
  );

export const deleteCustomLayer = async (
  fieldId: string,
  layerId: string
): Promise<{ ok: boolean }> =>
  api.delete<{ ok: boolean }>(`/api/v1/fields/${fieldId}/custom-layers/${layerId}`);
